import React from 'react';
import { Link } from 'react-router-dom';
import { QrCode, ScanLine, ShieldCheck, MapPin, Clock, AlertTriangle, CheckCircle2, MessageCircle, HelpCircle } from 'lucide-react';
import { COMPANY } from '../mock';

const Step = ({ n, title, children }) => (
  <div className="flex gap-4" data-testid={`guide-step-${n}`}>
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1b4f72] text-sm font-bold text-white">{n}</div>
    <div>
      <h4 className="font-display font-bold text-[#1b4f72]">{title}</h4>
      <p className="mt-1 text-[15px] leading-relaxed text-gray-600">{children}</p>
    </div>
  </div>
);

const Faq = ({ q, children }) => (
  <div className="rounded-2xl bg-white p-6 shadow-sm" data-testid="guide-faq-item">
    <h4 className="flex items-start gap-2 font-display font-bold text-[#1b4f72]"><HelpCircle size={18} className="mt-0.5 shrink-0 text-[#3a9b5c]" /> {q}</h4>
    <p className="mt-2 text-[15px] leading-relaxed text-gray-600">{children}</p>
  </div>
);

const PlatformGuide = () => {
  const wa = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`;
  return (
    <div data-testid="platform-guide-page">
      <section className="ea-hero-gradient py-14">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">Platform Guide &amp; Help</h1>
          <p className="mt-3 text-sm text-white/80">How the EA Lead Solutions marketplace, Digital ID and on-site QR verification work.</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-4xl space-y-14 px-5 lg:px-8">

          {/* Digital ID & QR overview */}
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-[#e9f3ec] p-2.5 text-[#1b4f72]"><ShieldCheck size={22} /></div>
              <h2 className="font-display text-2xl font-bold text-[#1b4f72]">Digital ID &amp; QR Verification</h2>
            </div>
            <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
              Every vetted operative is issued a unique registration number (<strong>EAL-XXXXX</strong>) and a Digital ID.
              On the day of the job, clients can confirm they are dealing with the right, vetted person by scanning a
              one-time QR code, right at the property. It takes seconds and gives both sides peace of mind.
            </p>
          </div>

          {/* For operatives */}
          <div className="rounded-3xl bg-[#f6faf7] p-8" data-testid="guide-operatives">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white p-2.5 text-[#1b4f72] shadow"><QrCode size={22} /></div>
              <h3 className="font-display text-xl font-bold text-[#1b4f72]">For Operatives — showing your QR</h3>
            </div>
            <div className="mt-6 space-y-5">
              <Step n="1" title="Open your Digital ID">Go to your <Link to="/dashboard/operative" className="font-semibold text-[#3a9b5c]">operative dashboard</Link>. Your Digital ID card shows your photo, name, trade, EAL registration number and verified status.</Step>
              <Step n="2" title="Generate your QR on site">When you arrive at the property, select the job and tap <strong>Generate QR</strong>. Allow location access when prompted — this proves you are physically at the job. Show the QR on your screen for the client to scan.</Step>
              <Step n="3" title="Codes are single-use">Each QR lasts <strong>5 minutes or one scan</strong>, whichever comes first, then expires. If it expires, just tap <strong>Regenerate QR</strong>. Never share codes or ask anyone else to attend for you — this is a serious breach of your Terms.</Step>
            </div>
          </div>

          {/* For clients */}
          <div className="rounded-3xl bg-[#eef4f8] p-8" data-testid="guide-clients">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white p-2.5 text-[#1b4f72] shadow"><ScanLine size={22} /></div>
              <h3 className="font-display text-xl font-bold text-[#1b4f72]">For Clients — scanning &amp; confirming</h3>
            </div>
            <div className="mt-6 space-y-5">
              <Step n="1" title="Open the scanner">On your confirmed job, tap <strong>Verify Operative ID</strong>, or go to the <Link to="/verify" className="font-semibold text-[#3a9b5c]">verification page</Link>. Choose <strong>Scan QR with Camera</strong> (allow camera and location access), or use the manual code entry if your camera is unavailable.</Step>
              <Step n="2" title="Scan the operative's QR">Point your camera at the QR code on the operative's screen. We instantly run a 6-point check and share your approximate location just for this one check, to confirm you and the operative are at the same place.</Step>
              <Step n="3" title="Read the result">A green <strong>VERIFIED</strong> result confirms the operative's identity for this job. A red <strong>NOT VERIFIED</strong> result means one or more checks failed (see below).</Step>
            </div>
          </div>

          {/* The 6-point check */}
          <div data-testid="guide-checks">
            <h3 className="font-display text-xl font-bold text-[#1b4f72]">The 6-point verification check</h3>
            <p className="mt-2 text-[15px] text-gray-600">A scan passes only when all six are satisfied:</p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                [ShieldCheck, 'Cryptographic validity', 'The code is genuinely issued by our platform and untampered.'],
                [Clock, 'Not expired', 'The code is within its 5-minute window.'],
                [CheckCircle2, 'Not previously used', 'The code has not already been scanned.'],
                [ShieldCheck, 'Operative assigned to this job', 'The code belongs to the operative booked for your job.'],
                [ShieldCheck, 'Account active', 'The operative is vetted and not suspended or banned.'],
                [MapPin, 'Within 500m of the property', 'The operative generated the code where you are scanning it.'],
              ].map(([Icon, t, d], i) => (
                <li key={i} className="flex gap-3 rounded-xl bg-white p-4 shadow-sm">
                  <Icon size={18} className="mt-0.5 shrink-0 text-[#3a9b5c]" />
                  <div><p className="text-sm font-semibold text-[#1b4f72]">{t}</p><p className="text-sm text-gray-500">{d}</p></div>
                </li>
              ))}
            </ul>
          </div>

          {/* What happens if verification fails */}
          <div className="rounded-3xl border border-red-100 bg-red-50/60 p-8" data-testid="guide-failure">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-white p-2.5 text-red-600 shadow"><AlertTriangle size={22} /></div>
              <h3 className="font-display text-xl font-bold text-[#1b4f72]">What happens if verification fails?</h3>
            </div>
            <ul className="mt-5 space-y-3 text-[15px] leading-relaxed text-gray-700">
              <li>• <strong>Do not let the work start</strong> until you are confident of the operative's identity.</li>
              <li>• A common cause is an <strong>expired code</strong> — ask the operative to tap <strong>Regenerate QR</strong> and scan again.</li>
              <li>• If it still fails (wrong operative, account not active, or the locations are too far apart), contact us on WhatsApp straight away.</li>
              <li>• Every failed scan <strong>automatically alerts our admin team</strong> and is saved to a permanent audit trail against your job reference.</li>
              <li>• If a dispute follows (for example a no-show or misconduct complaint), this verification record is used as primary evidence in our review.</li>
            </ul>
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="guide-wa-fail" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90"><MessageCircle size={16} /> Message us on WhatsApp</a>
          </div>

          {/* Privacy note */}
          <div className="rounded-2xl bg-[#e9f3ec] p-6" data-testid="guide-privacy">
            <h3 className="font-display font-bold text-[#1b4f72]">Your location &amp; privacy</h3>
            <p className="mt-2 text-[15px] leading-relaxed text-gray-600">
              We capture location only at the moment a QR is generated (operative) and scanned (client), purely to confirm on-site presence.
              Verification records — the result, the six checks, both locations, the distance and a timestamp — are kept as an audit trail for
              safety, fraud prevention, dispute resolution and legal compliance under the UK GDPR. See the
              <Link to="/client-terms?ref=request-form" className="font-semibold text-[#3a9b5c]"> Client</Link> and
              <Link to="/freelancer-terms?ref=join-form" className="font-semibold text-[#3a9b5c]"> Freelancer</Link> Terms for full detail.
            </p>
          </div>

          {/* FAQ incl WhatsApp-only contact */}
          <div data-testid="guide-faq">
            <h2 className="font-display text-2xl font-bold text-[#1b4f72]">Frequently asked questions</h2>
            <div className="mt-6 space-y-4">
              <Faq q="How do I contact support?">
                Support is available on <strong>WhatsApp only</strong>, during our support hours of <strong>{COMPANY.whatsappHours}</strong>.
                We no longer publish a phone number — this keeps support focused and responses quick. If a matter genuinely needs a call,
                we will arrange one with you via WhatsApp first. <a href={wa} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#3a9b5c]">Message us on WhatsApp</a>.
              </Faq>
              <Faq q="Do I need to verify the operative every visit?">
                We recommend verifying on the first attendance for each job, and any time a different person arrives. Each visit can be logged for your records.
              </Faq>
              <Faq q="The camera won't open — can I still verify?">
                Yes. On the verification page choose manual code entry and type or paste the operative's code. The same 6-point check runs either way.
              </Faq>
              <Faq q="Why is my location needed?">
                Only to confirm the operative is genuinely at your property. The operative's code-generation location must be within 500m of where you scan.
                Location is used for that single check and stored in the verification record.
              </Faq>
              <Faq q="How do payments work?">
                All payments are made securely on the platform via Stripe — a 15% deposit when you select an operative, and the remaining 85% when the job is complete.
                Never pay an operative directly; doing so voids your buyer protection.
              </Faq>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default PlatformGuide;
